"use client";

import { useState, useEffect, useCallback } from "react";
import { apiFetch, versioned } from "@/lib/api";

/**
 * useMyCourses — fetches the authenticated user's enrolled course codes
 * from their profile (profiles.courses_field).
 *
 * Backend_reality: there is no dedicated /user/courses endpoint.
 * Courses are stored as a string[] on the profile via
 * PATCH /api/v1/user/profile { courses: [...] }.
 * The GET /api/v1/user/profile endpoint returns the full profile
 * including the `courses` array.
 *
 * Usage:
 *   const { courses, courseSet, loading, refetch, addCourse, removeCourse } = useMyCourses();
 *   // `courses` is string[] of course codes e.g. ["CSC201", "PHY101"]
 */
export function useMyCourses() {
  const [courses, setCourses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const profile = await apiFetch<Record<string, unknown>>(
        versioned("/api/v1/user/profile"),
      );
      const c = (profile as any)?.courses;
      setCourses(Array.isArray(c) ? c : []);
      setError(null);
    } catch (err) {
      setError(err as Error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const addCourse = useCallback(
    async (courseId: string) => {
      if (courses.includes(courseId)) return false;
      const updated = [...courses, courseId];
      await apiFetch(versioned("/api/v1/user/profile"), {
        method: "PATCH",
        body: JSON.stringify({ courses: updated }),
      });
      setCourses(updated);
      return true;
    },
    [courses],
  );

  const removeCourse = useCallback(
    async (courseId: string) => {
      const updated = courses.filter((c) => c !== courseId);
      await apiFetch(versioned("/api/v1/user/profile"), {
        method: "PATCH",
        body: JSON.stringify({ courses: updated }),
      });
      setCourses(updated);
    },
    [courses],
  );

  const refetch = useCallback(() => {
    return fetchCourses();
  }, [fetchCourses]);

  return {
    courses,
    courseSet: new Set(courses),
    hasCourses: courses.length > 0,
    loading,
    error,
    refetch,
    addCourse,
    removeCourse,
  };
}
